// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import type { CustomerContact, EmailConfidence } from '@crm-src/lib/customer-contacts-api'

// Re-exported so callers can keep importing the confidence type from the lead
// domain; the canonical definition lives next to CustomerContact.
export type { EmailConfidence }

/** The billing identity a reseller-issued document is drawn in the name of
 * (spec A §3.2). Nested rather than flattened onto `Reseller` so a consumer
 * that only wants the trade identity can ignore one key. `null` when the
 * columns have not been migrated yet — crm-api guards the whole block. */
export interface ResellerBillingIdentity {
  legal_name?: string | null
  tax_id?: string | null
  currency?: string | null
  payment_terms?: string | null
  invoice_footer?: string | null
  billing_email?: string | null
  billing_phone?: string | null
  logo_attached?: boolean
}

export interface Reseller {
  id: string
  name: string
  code?: string
  website?: string
  address?: string
  city?: string
  state?: string
  postal_code?: string
  country?: string
  created_at: string
  updated_at: string
  dealer_count?: number
  /** Reseller-of-reseller (spec A §3.1). A parent sees its descendants'
   * customers, so this edge is the shape of the CRM scope wall — crm-api
   * validates cycles, cross-tenant parents and a depth cap of 5. */
  parent_reseller_id?: string | number | null
  parent?: { id: string | number; name: string; code?: string | null } | null
  billing?: ResellerBillingIdentity | null
  created_by?: {
    id: string
    full_name: string
    email: string
  } | null
  assigned_salespeople?: Array<{
    id: string
    full_name: string
    email: string
  }>
}

export interface Product {
  id: string
  name: string
  code?: string
  created_at: string
  updated_at: string
}

export interface ProductFeature {
  id: string
  name: string
  code?: string
  product: Product
  created_at: string
  updated_at: string
}

export interface Lead {
  id: string
  customer_id?: string
  type: string
  first_name: string
  last_name: string
  full_name: string
  email: string
  email2?: string
  phone?: string
  phone2?: string
  mobile?: string
  company_name?: string
  job_title?: string
  source?: string
  /** Industry / sector (Aviation & Airports, Nuclear, Military & Defense, …).
   * Set by the sales agent's vertical on AI-generated prospects; editable in
   * the prospect form. Options + display: `@crm-src/lib/industries`. */
  industry?: string
  status?: LeadStatus
  priority: LeadPriority
  prospected_value: number
  prospected_ports: number
  business_category?: string
  reseller?: Reseller | null
  dealers?: Array<{
    id: string
    name: string
    address?: string
    city?: string
    state?: string
    postal_code?: string
    country?: string
  }>
  
  // Product information
  product?: Product | null
  product_feature?: ProductFeature | null
  assigned_salespeople?: Array<{
    id: string
    user_id: string
    full_name: string
    email: string
    role: string
    assigned_at: string
    assigned_by?: {
      id: string
      full_name: string
      email: string
    }
  }>
  salespeople_names?: string

  website?: string
  address?: string
  city?: string
  state?: string
  country?: string
  postal_code?: string
  timezone?: string
  location?: string
  notes?: string
  starred?: boolean
  next_follow_up_at?: string
  owner?: {
    id: string
    name: string
    email: string
  }
  /** Contact persons on this lead (buyers). AI-generated prospects keep their
   * emails here, not on the blank top-level `email`. Always returned by the
   * leads serializer. */
  customer_contacts?: CustomerContact[]
  created_at: string
  updated_at: string
}

export interface DetailedLead extends Lead {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  data_quality_score: number
  email_verified: boolean
  phone_verified: boolean
  do_not_email: boolean
  do_not_call: boolean
  marketing_consent: boolean
  converted_contact_id?: string
  converted_deal_id?: string
  recent_activities: LeadActivity[]
  days_since_created: number
  days_since_last_activity: number
  needs_follow_up: boolean
  overdue_follow_up: boolean
}

export interface LeadActivity {
  id: string
  title: string
  activity_type: string
  description?: string
  status: string
  user: {
    id: string
    name: string
  }
  created_at: string
}

export interface CreateLeadData {
  // Customer Profile fields
  first_name: string
  last_name: string
  email: string
  email2?: string
  phone?: string
  phone2?: string
  mobile?: string
  company_name?: string
  job_title?: string
  website?: string
  address?: string
  city?: string
  state?: string
  country?: string
  postal_code?: string
  timezone?: string
  
  // Customer/Lead specific fields
  prospect_source?: string
  industry?: string
  priority?: LeadPriority
  prospected_value?: number
  prospected_ports?: number
  business_category?: string
  reseller_id?: string | null
  dealer_ids?: string[]
  assigned_to_id?: string
  notes?: string
  
  // Product selection fields
  product_id?: string
  product_feature_id?: string
}

export interface UpdateLeadData extends Partial<CreateLeadData> {
  status?: LeadStatus
}

export interface LeadsResponse {
  leads: Lead[]
  pagination: {
    current_page: number
    total_pages: number
    total_count: number
    per_page: number
  }
  stats?: LeadStats
}

export interface LeadStats {
  // Main stats for cards
  total_prospects: number
  high_priority_prospects: number
  total_prospected_value: number
  urgent_priority_prospects: number
  
  // Additional data
  average_prospected_value: number
  recent_leads: number
  generated_at: string
}

export interface LeadFilters {
  search?: string
  status?: LeadStatus
  /**
   * Priority facet. A plain `string`, not `LeadPriority`, because the filter
   * bar is MULTI-select and sends the values comma-separated
   * ("low,urgent") for the server to turn into an IN clause.
   */
  priority?: string
  source?: string
  /** Split AI-generated prospects (born from the autonomous sales agent's
   * "Convert to Prospect") from human-entered ones. Omit for all prospects;
   * `true` → only AI-generated; `false` → only regular. Not a user-facing
   * facet: each Prospects page PINS it (see `pages/prospects/prospects-origin`). */
  ai_generated?: boolean
  // The four remaining facets `Api::V1::LeadsController.filterable` implements.
  // Comma-separated for multi-select; `reseller_id=none` means "direct".
  industry?: string
  prospect_source?: string
  reseller_id?: string
  salesperson_id?: string
  owner_id?: string
  assigned_to_id?: string

  min_value?: number
  max_value?: number
  starred?: boolean
  active?: boolean
  created_after?: string
  created_before?: string
  last_activity_after?: string
  exclude_converted?: boolean
  follow_up_before?: string
  tags?: string
  /**
   * Canonical sort params. `Api::V1::LeadsController` reads `sort` + `dir`
   * (and still accepts the legacy `direction` alias). The old
   * `sort_by`/`sort_direction` names are gone: NOTHING sent them and the
   * controller never read them — see the 2026-08-28 table sorting spec. Keys
   * must appear in `PROSPECTS_TABLE.columns`, which `config-parity.test.ts`
   * holds against the controller's `sortable_columns`.
   */
  sort?: string
  dir?: 'asc' | 'desc'
  page?: number
  per_page?: number
  include_stats?: boolean
}

export interface BulkUpdateLeads {
  lead_ids: string[]
  updates: {
    status?: LeadStatus
    priority?: LeadPriority
    owner_id?: string
    assigned_to_id?: string
    starred?: boolean
    do_not_call?: boolean
    do_not_email?: boolean
    marketing_consent?: boolean
    next_follow_up_at?: string
  }
}

export interface LeadConversion {
  lead_id: string
  contact?: {
    // Additional contact-specific fields can be added here
    relationship_status?: string
    lifecycle_stage?: string
  }
}

export interface LeadDuplicate {
  master_lead: Lead
  duplicate_leads: Lead[]
  confidence_score: number
}

// Enums and types
export type LeadStatus = 'new' | 'contacted' | 'in_progress' | 'qualified' | 'unqualified' | 'converted' | 'lost'
export type LeadPriority = 'low' | 'medium' | 'high' | 'urgent'

const LEAD_STATUSES: { value: LeadStatus; label: string; color: string; description: string }[] = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-800', description: 'Fresh lead - needs first contact' },
  { value: 'contacted', label: 'Contacted', color: 'bg-yellow-100 text-yellow-800', description: 'Initial contact made - schedule follow-up' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-orange-100 text-orange-800', description: 'Active conversations - qualifying needs' },
  { value: 'qualified', label: 'Qualified', color: 'bg-green-100 text-green-800', description: 'Real opportunity - ready to convert' },
  { value: 'unqualified', label: 'Unqualified', color: 'bg-red-100 text-red-800', description: 'No fit - wrong budget/timing/need' },
  { value: 'converted', label: 'Converted', color: 'bg-purple-100 text-purple-800', description: 'Moved to deals pipeline' },
  { value: 'lost', label: 'Lost', color: 'bg-gray-100 text-gray-800', description: 'Lost to competitor or no decision' }
]

export const LEAD_PRIORITIES: { value: LeadPriority; label: string; color: string }[] = [
  { value: 'low', label: 'Low', color: 'bg-green-100 text-green-800' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-800' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-800' }
]

export const LEAD_SOURCES: string[] = [
  'Website',
  'Referral',
  'Social Media',
  'Email',
  'Email Campaign',
  'Cold Call',
  'Trade Show',
  'Advertisement',
  'Partner',
  'Import',
  'Other'
]

// Utility functions
export const getLeadStatusColor = (status: LeadStatus): string => {
  const statusConfig = LEAD_STATUSES.find(s => s.value === status)
  return statusConfig?.color || 'bg-gray-100 text-gray-800'
}

export const getLeadPriorityColor = (priority: LeadPriority): string => {
  const priorityConfig = LEAD_PRIORITIES.find(p => p.value === priority)
  return priorityConfig?.color || 'bg-gray-100 text-gray-800'
}



